/**
 * Outbox — what is waiting to go out, what failed, and the hourly budget.
 *
 * Auto-refreshes while the screen is open: during a send the interesting part
 * is watching the backlog drain, and a manual refresh button gets old fast.
 * The refresh keeps the page and the selection, so a user picking emails to
 * cancel is not thrown back to the first page every 15 seconds.
 *
 * Pending and failed emails can be cancelled one by one, by selection, or all
 * at once (a cancelled failure no longer comes back with "Retry all failed").
 * A cancelled email can be sent again from here (as it was rendered); for a
 * personalised campaign the recipient also becomes selectable again on the
 * campaign's page, where a new send re-renders it with their current image.
 */
define('mailingQueueView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  var REFRESH_MS = 15000;

  /** Waiting or failed: what the backend lets a user cancel. */
  function cancellable(item) {
    var status = item.status || 'PENDING';
    return status === 'PENDING' || status === 'FAILED';
  }
  var PAGE_SIZE = 50;

  var QueueView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-queue',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-tiles queue-tiles"></div>' +
      '<div class="mlg-toolbar">' +
      '  <select class="f-status">' +
      '    <option value="">All statuses</option>' +
      '    <option value="PENDING">Pending</option>' +
      '    <option value="SENDING">Sending</option>' +
      '    <option value="SENT">Sent</option>' +
      '    <option value="FAILED">Failed</option>' +
      '    <option value="SKIPPED">Skipped</option>' +
      '    <option value="CANCELLED">Cancelled</option>' +
      '  </select>' +
      '  <span class="mlg-count"></span>' +
      '  <button type="button" class="mlg-btn danger mlg-cancel-selected" disabled>Cancel selected</button>' +
      '</div>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading outbox…</div></div>' +
      '<div class="mlg-pager"></div>'
    ),

    ui: {
      tiles: '.queue-tiles',
      body: '.mlg-body',
      status: '.f-status',
      count: '.mlg-count',
      cancelSelected: '.mlg-cancel-selected',
      pager: '.mlg-pager'
    },

    events: {
      'change .f-status': 'onFilter',
      'click .mlg-retry-failed': 'onRetryFailed',
      'click .mlg-cancel-all': 'onCancelAll',
      'click .mlg-cancel-all-failed': 'onCancelAllFailed',
      'click .mlg-cancel-selected': 'onCancelSelected',
      'click .mlg-cancel': 'onCancel',
      'click .mlg-resend': 'onResend',
      'change .mlg-select-row': 'onSelectRow',
      'change .mlg-select-page': 'onSelectPage',
      'click .mlg-page': 'onPage'
    },

    initialize: function () {
      this.page = 1;
      this.selected = {};
      this.items = [];
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('queue',
          '<button type="button" class="mlg-btn danger mlg-cancel-all">Cancel all pending</button> ' +
          '<button type="button" class="mlg-btn danger mlg-cancel-all-failed">Cancel all failed</button> ' +
          '<button type="button" class="mlg-btn mlg-retry-failed">Retry all failed</button>')
      };
    },

    onShow: function () {
      var self = this;
      this.load();
      this.timer = window.setInterval(function () { self.load(); }, REFRESH_MS);
    },

    onDestroy: function () {
      if (this.timer) {
        window.clearInterval(this.timer);
      }
    },

    load: function () {
      var self = this;
      var status = this.ui.status.val();
      $.when(
        MailingApi.queueStats(),
        MailingApi.queue({ status: status, limit: PAGE_SIZE, page: this.page - 1 }),
        MailingApi.queueCount({ status: status })
      ).done(function (stats, items, count) {
        var total = (count || {}).total || 0;
        var pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        // Emails left the page since (sent, cancelled elsewhere): step back to a page that exists.
        if (self.page > pages) {
          self.page = pages;
          self.load();
          return;
        }
        self.items = items || [];
        self.total = total;
        self.renderTiles(stats);
        self.renderList();
        self.renderPager(pages);
      }).fail(function (xhr) {
        self.ui.body.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    renderTiles: function (stats) {
      var budget = stats.sentLastHour + ' / ' + stats.maxPerHour;
      var tiles = [
        { label: 'Pending', value: Shell.formatNumber(stats.pending) },
        { label: 'Sending', value: Shell.formatNumber(stats.sending) },
        { label: 'Sent today', value: Shell.formatNumber(stats.sentToday) },
        { label: 'Failed today', value: Shell.formatNumber(stats.failedToday),
          tone: stats.failedToday > 0 ? 'bad' : '' },
        { label: 'Last hour', value: budget,
          sub: 'hourly limit · ' + stats.delaySeconds + 's between sends',
          tone: stats.sentLastHour >= stats.maxPerHour ? 'warn' : '' }
      ];
      this.ui.tiles.html(_.map(tiles, function (tile) {
        return '<div class="mlg-tile ' + (tile.tone || '') + '">' +
          '<div class="t-value">' + tile.value + '</div>' +
          '<div class="t-label">' + _.escape(tile.label) + '</div>' +
          (tile.sub ? '<div class="t-sub">' + _.escape(tile.sub) + '</div>' : '') +
          '</div>';
      }).join(''));
    },

    /** A waiting or a failed email can be cancelled, so only those can be selected. */
    pendingOnPage: function () {
      return _.filter(this.items, function (item) { return cancellable(item); });
    },

    renderList: function () {
      var self = this;
      // A selected email that is no longer pending here (sent meanwhile) drops out of the selection.
      var pendingIds = _.map(this.pendingOnPage(), function (item) { return String(item.secId); });
      this.selected = _.pick(this.selected, pendingIds);
      this.updateSelectionUi();

      var first = this.total ? (this.page - 1) * PAGE_SIZE + 1 : 0;
      var last = Math.min(this.total, this.page * PAGE_SIZE);
      this.ui.count.text(this.total ? Shell.formatNumber(first) + '–' + Shell.formatNumber(last) + ' of ' +
        Shell.formatNumber(this.total) : '');

      if (!this.items.length) {
        this.ui.body.html(Shell.emptyHtml(this.ui.status.val() ? 'No email with this status.' : 'The outbox is empty.'));
        return;
      }
      var rows = _.map(this.items, function (item) {
        var status = item.status || 'PENDING';
        var pending = cancellable(item);
        var checked = pending && self.selected[String(item.secId)];
        // "a@x.com; b@y.com" would only fail again: fixed on the campaign, not resent from here.
        var multiAddress = /[;,]/.test(item.toEmail || '');
        return '<tr data-secid="' + item.secId + '">' +
          '<td class="c-check">' + (pending ?
            '<input type="checkbox" class="mlg-select-row"' + (checked ? ' checked' : '') + '>' : '') + '</td>' +
          '<td>' + _.escape(item.toEmail || '') + '</td>' +
          '<td>' + _.escape(item.subject || '') +
            '<div class="c-sub">' + _.escape(item.sourceName || '') + '</div></td>' +
          '<td><span class="mlg-chip st-' + status.toLowerCase() + '">' +
            Shell.prettyCase(status) + '</span>' +
            (item.attempts > 1 ? ' <span class="c-count">' + item.attempts + ' tries</span>' : '') + '</td>' +
          '<td>' + Shell.formatDate(item.sentAt || item.scheduledAt, true) + '</td>' +
          // For a skipped or cancelled item this column holds the reason, not an error.
          '<td class="c-error" title="' + _.escape(item.errorMessage || '') + '">' +
            _.escape(item.errorMessage || '') + '</td>' +
          '<td class="c-actions">' +
            (pending ? '<button type="button" class="mlg-btn small danger mlg-cancel">Cancel</button>' : '') +
            (status === 'CANCELLED' && !multiAddress ? '<button type="button" class="mlg-btn small mlg-resend" ' +
              'title="Back in the outbox, exactly as it was rendered">Send again</button>' : '') +
            (status === 'CANCELLED' && multiAddress ? '<span class="c-sub" title="Several addresses in one field: ' +
              'use &quot;Fix multi-address recipients&quot; on its personalised campaign">Several addresses</span>' : '') +
          '</td></tr>';
      }).join('');

      var allChecked = pendingIds.length > 0 && _.size(this.selected) === pendingIds.length;
      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr><th class="c-check">' + (pendingIds.length ?
          '<input type="checkbox" class="mlg-select-page" title="Select the pending and failed emails of this page"' +
          (allChecked ? ' checked' : '') + '>' : '') + '</th>' +
        '<th>To</th><th>Subject</th><th>Status</th><th>When</th><th>Error</th><th></th></tr></thead>' +
        '<tbody>' + rows + '</tbody></table>');
    },

    renderPager: function (pages) {
      if (pages <= 1) {
        this.ui.pager.empty();
        return;
      }
      var current = this.page;
      var first = Math.max(1, current - 3);
      var last = Math.min(pages, first + 6);
      first = Math.max(1, last - 6);
      var buttons = '';
      if (current > 1) {
        buttons += '<button type="button" class="mlg-page" data-page="' + (current - 1) + '">‹</button>';
      }
      if (first > 1) {
        buttons += '<button type="button" class="mlg-page" data-page="1">1</button>' +
          (first > 2 ? '<span class="mlg-page-gap">…</span>' : '');
      }
      for (var page = first; page <= last; page++) {
        buttons += '<button type="button" class="mlg-page' + (page === current ? ' active' : '') +
          '" data-page="' + page + '">' + page + '</button>';
      }
      if (last < pages) {
        buttons += (last < pages - 1 ? '<span class="mlg-page-gap">…</span>' : '') +
          '<button type="button" class="mlg-page" data-page="' + pages + '">' + pages + '</button>';
      }
      if (current < pages) {
        buttons += '<button type="button" class="mlg-page" data-page="' + (current + 1) + '">›</button>';
      }
      this.ui.pager.html(buttons);
    },

    updateSelectionUi: function () {
      var count = _.size(this.selected);
      this.ui.cancelSelected.prop('disabled', count === 0)
        .text(count ? 'Cancel selected (' + count + ')' : 'Cancel selected');
    },

    onFilter: function () {
      this.page = 1;
      this.selected = {};
      this.load();
    },

    onPage: function (e) {
      this.page = Number($(e.currentTarget).data('page')) || 1;
      this.selected = {};
      this.load();
    },

    onSelectRow: function (e) {
      var secId = String($(e.currentTarget).closest('tr').data('secid'));
      if (e.currentTarget.checked) {
        this.selected[secId] = true;
      } else {
        delete this.selected[secId];
      }
      var pending = this.pendingOnPage().length;
      this.$('.mlg-select-page').prop('checked', pending > 0 && _.size(this.selected) === pending);
      this.updateSelectionUi();
    },

    onSelectPage: function (e) {
      var self = this;
      var checked = e.currentTarget.checked;
      this.selected = {};
      if (checked) {
        _.each(this.pendingOnPage(), function (item) { self.selected[String(item.secId)] = true; });
      }
      this.$('.mlg-select-row').prop('checked', checked);
      this.updateSelectionUi();
    },

    /** Shared by the bulk buttons: same banner, same reload. */
    afterCancel: function (request) {
      var self = this;
      request.done(function (result) {
        self.selected = {};
        Shell.banner(self.$el, (result.cancelled || 0) + ' email(s) cancelled', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onCancelSelected: function () {
      var secIds = _.keys(this.selected);
      if (!secIds.length || !window.confirm('Cancel the ' + secIds.length + ' selected email(s)? ' +
          'They stay in the outbox as "Cancelled" and can be sent again.')) {
        return;
      }
      this.afterCancel(MailingApi.cancelQueueItems(secIds));
    },

    onCancelAll: function () {
      if (!window.confirm('Cancel EVERY email still pending in the outbox, all campaigns and workflows included?\n\n' +
          'Emails being sent right now are not stopped. Cancelled emails can be sent again.')) {
        return;
      }
      this.afterCancel(MailingApi.cancelAllPendingQueueItems());
    },

    onCancelAllFailed: function () {
      if (!window.confirm('Cancel EVERY failed email of the outbox?\n\n' +
          '"Retry all failed" will no longer bring them back. For a personalised campaign, the recipient ' +
          'can then be selected and sent again from the campaign.')) {
        return;
      }
      this.afterCancel(MailingApi.cancelAllFailedQueueItems());
    },

    onRetryFailed: function () {
      var self = this;
      if (!window.confirm('Put every failed email back in the queue? Cancelled emails are not included.')) { return; }

      MailingApi.retryFailed().done(function (result) {
        Shell.banner(self.$el, (result.requeued || 0) + ' email(s) requeued', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onCancel: function (e) {
      var self = this;
      var secId = String($(e.currentTarget).closest('tr').data('secid'));
      MailingApi.cancelQueueItem(secId).done(function () {
        delete self.selected[secId];
        Shell.banner(self.$el, 'Email cancelled — it can be sent again', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onResend: function (e) {
      var self = this;
      var secId = String($(e.currentTarget).closest('tr').data('secid'));
      MailingApi.resendQueueItem(secId).done(function () {
        Shell.banner(self.$el, 'Email back in the outbox', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    }
  });

  module.exports = QueueView;
});

